import React, { useState, useEffect } from 'react';
import { Bell } from 'lucide-react';
import { getRealtimeManager } from '../services/realtimeService';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';

const NotificationsDropdown = ({ isOpen, onClose }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const realtimeManager = getRealtimeManager();
    if (!realtimeManager) return;

    setNotifications(realtimeManager.getNotifications());
    setUnreadCount(realtimeManager.getUnreadCount());

    const handleRealtimeUpdate = (event, data) => {
      switch (event) {
        case 'notifications_loaded':
        case 'new_notification':
        case 'notification_updated':
        case 'notification_read':
        case 'all_notifications_read':
          setNotifications(data.notifications);
          setUnreadCount(data.unreadCount);
          break;
        default:
          break;
      }
    };

    realtimeManager.addListener(handleRealtimeUpdate);

    return () => {
      realtimeManager.removeListener(handleRealtimeUpdate);
    };
  }, []);

  const handleMarkAsRead = async (notificationId) => {
    setLoading(true);
    try {
      const realtimeManager = getRealtimeManager();
      if (realtimeManager) {
        await realtimeManager.markAsRead(notificationId);
      }
    } catch (error) {
      console.error('Error marking notification as read:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAllAsRead = async () => {
    setLoading(true);
    try {
      const realtimeManager = getRealtimeManager();
      if (realtimeManager) {
        await realtimeManager.markAllAsRead();
      }
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInMinutes = Math.floor((now - date) / (1000 * 60));

    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return date.toLocaleDateString();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50" onClick={onClose}>
      <Card
        className="absolute end-4 top-16 w-96 overflow-hidden shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader className="flex-row items-center justify-between space-y-0 p-4">
          <CardTitle className="flex items-center gap-2 text-base">
            Notifications
            {unreadCount > 0 ? <Badge variant="destructive">{unreadCount}</Badge> : null}
          </CardTitle>
          <div className="flex gap-2">
            {unreadCount > 0 ? (
              <Button
                type="button"
                size="sm"
                variant="secondary"
                onClick={handleMarkAllAsRead}
                disabled={loading}
              >
                Mark All Read
              </Button>
            ) : null}
            <Button type="button" size="sm" variant="ghost" onClick={onClose}>
              Close
            </Button>
          </div>
        </CardHeader>
        <Separator />
        <CardContent className="p-0">
          <ScrollArea className="h-80">
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center p-8 text-center text-muted-foreground">
                <Bell className="mb-2 h-8 w-8" />
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <button
                  key={notification.id}
                  type="button"
                  className={cn(
                    'flex w-full items-start gap-3 border-b p-4 text-start transition-colors hover:bg-muted/50',
                    !notification.read_at && 'bg-primary/5'
                  )}
                  onClick={() => !notification.read_at && handleMarkAsRead(notification.id)}
                >
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-muted">
                    <Bell className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <h4 className="text-sm font-medium text-foreground">{notification.title}</h4>
                        <p className="mt-1 text-sm text-muted-foreground">{notification.body}</p>
                        <p className="mt-1 text-xs text-muted-foreground">
                          {formatTime(notification.sent_at)}
                        </p>
                      </div>
                      {!notification.read_at ? (
                        <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-destructive" />
                      ) : null}
                    </div>
                  </div>
                </button>
              ))
            )}
          </ScrollArea>
        </CardContent>
        {notifications.length > 0 ? (
          <>
            <Separator />
            <CardFooter className="justify-center p-3">
              <p className="text-xs text-muted-foreground">
                {notifications.length} notification{notifications.length !== 1 ? 's' : ''}
              </p>
            </CardFooter>
          </>
        ) : null}
      </Card>
    </div>
  );
};

export default NotificationsDropdown;
